import { useTObject, useTOptions } from "@/i18n"
import { DataDrivenFormSchema } from "@/lib/data-driven-forms/schema"
import { Globe, Lock } from "lucide-react"
import { useTranslation } from "react-i18next"
import { reliefListingSchema } from "./validation-schema"
import { typedEntries } from "@/types/helpers"
import { TagHierarchy } from "@/types/tag-hierarchy"
import { ParseKeys } from "i18next"

export function useFormSchema() {
  const tagCategories = useTObject(
    "tag_categories",
    "listing_profile_tags",
  ) as unknown as TagHierarchy

  const formSchema: DataDrivenFormSchema<
    ["school_relief_listings", "listing_profile_tags"]
  > = {
    type: "form",
    namespace: ["school_relief_listings", "listing_profile_tags"],
    validator: reliefListingSchema,
    properties: [
      {
        type: "section:hash",
        sectionKey: "details",
        title: "form.sections.details",
        properties: [
          {
            type: "section:scrolling",
            sectionKey: "general_information",
            title: "form.sections.general_information",
            properties: [
              {
                type: "field:input",
                inputType: "text",
                name: "details.title",
                label: "form.general_information_section.title",
              },
              {
                type: "field:textarea",
                name: "details.description",
                label: "form.general_information_section.description",
              },
              {
                type: "field:radioGroup",
                name: "invite_only",
                label: "form.general_information_section.invite_only",
                useOptions: () => {
                  const { t } = useTranslation("school_relief_listings")

                  return [
                    {
                      label: t("privacy_levels.invite_only"),
                      description: t("privacy_levels.invite_only_description"),
                      value: "true",
                      icon: Lock,
                    },
                    {
                      label: t("privacy_levels.anyone_can_apply"),
                      description: t(
                        "privacy_levels.anyone_can_apply_description",
                      ),
                      value: "false",
                      icon: Globe,
                    },
                  ]
                },
              },
              {
                type: "field:custom:subject",
                name: "details.subject_key",
                label: "form.general_information_section.subject",
                curriculaLabel: "form.general_information_section.curriculum",
              },
              {
                type: "field:input",
                inputType: "date",
                name: "details.start_date",
                label: "form.general_information_section.starts_date",
              },
              {
                type: "field:input",
                inputType: "date",
                name: "details.end_date",
                label: "form.general_information_section.end_date",
              },
              {
                type: "field:input",
                inputType: "email",
                name: "details.contact_details.email",
                label: "form.general_information_section.contact_email",
              },
              {
                type: "field:input",
                inputType: "text",
                name: "details.contact_details.name",
                label: "form.general_information_section.contact_name",
              },
            ],
          },
          {
            type: "section:scrolling",
            sectionKey: "location",
            title: "form.sections.location",
            properties: [
              {
                type: "field:combobox",
                comboboxType: "multi",
                name: "details.delivery_mode",
                label: "form.location_section.delivery",
                useOptions: () =>
                  useTOptions("delivery_modes", "school_relief_listings"),
              },
              {
                type: "field:place",
                name: "details.address",
                label: "form.location_section.address",
              },
            ],
          },
          {
            type: "section:scrolling",
            sectionKey: "timetable",
            title: "form.sections.timetable",
            properties: [
              {
                type: "field:combobox",
                comboboxType: "single",
                name: "details.timetable.timezone_id",
                label: "form.timetable_section.timezone",
                useOptions: () =>
                  Intl.supportedValuesOf("timeZone").map(
                    (timezone: string) => ({
                      label: timezone,
                      value: timezone,
                    }),
                  ),
              },
              {
                type: "field:custom:array",
                name: "details.timetable.sessions",
                label: "form.timetable_section.sessions",
                addLabel: "form.timetable_section.sessions_add_label",
                removeLabel: "form.timetable_section.sessions_remove_label",
                defaultValue: {
                  day: "monday",
                  week: "week_one",
                  start_time: "09:00",
                  end_time: "10:00",
                },
                properties: [
                  {
                    type: "field:combobox",
                    label: "form.timetable_section.day",
                    name: "day",
                    comboboxType: "single",
                    useOptions: () =>
                      useTOptions("days", "school_relief_listings"),
                  },
                  {
                    type: "field:combobox",
                    comboboxType: "single",
                    name: "week",
                    label: "form.timetable_section.week",
                    useOptions: () =>
                      useTOptions("weeks", "school_relief_listings"),
                  },
                  {
                    type: "field:input",
                    inputType: "time",
                    label: "form.timetable_section.start_time",
                    name: "start_time",
                  },
                  {
                    type: "field:input",
                    inputType: "time",
                    label: "form.timetable_section.end_time",
                    name: "end_time",
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "section:hash",
        sectionKey: "listing_tags",
        title: "form.sections.tags",
        infoContentKey: "form.tags_section.section_description",
        properties: typedEntries(tagCategories).map(
          ([tagCategoryKey, category]) => ({
            type: "section:scrolling",
            sectionKey: tagCategoryKey,
            title:
              `listing_profile_tags:tag_categories.${tagCategoryKey}.name` as ParseKeys<
                ["school_relief_listings", "listing_profile_tags"]
              >,
            infoContentKey:
              `listing_profile_tags:tag_categories.${tagCategoryKey}.description` as ParseKeys<
                ["school_relief_listings", "listing_profile_tags"]
              >,
            properties: typedEntries(category.tag_subcategories).flatMap(
              ([tagSubCategoryKey, subcategory]) =>
                typedEntries(subcategory.tags).map(([tagKey, _tag]) => ({
                  type: "field:custom:toggle",
                  name: "details.tag_ids",
                  label:
                    `listing_profile_tags:tag_categories.${tagCategoryKey}.tag_subcategories.${tagSubCategoryKey}.tags.${tagKey}.name` as ParseKeys<
                      ["school_relief_listings", "listing_profile_tags"]
                    >,
                  value: tagKey,
                  isArray: true,
                })),
            ),
          }),
        ),
      },
    ],
  }
  return formSchema
}
